import React, { useState, useEffect } from 'react';
import { Dropdown, IDropdownOption, SearchBox } from '@fluentui/react';
import { Circle } from '../../services/microsoft/msGraphApi';

export interface CircleSelectorProps {
    circles: Circle[];
    selectedCircleIds: string[];
    onSelectionChange: (selectedCircles: Circle[]) => void;
    searchPlaceholder?: string;
    dropdownPlaceholder?: string;
    searchDebounceMs?: number;
    isMultiSelect: boolean;
    isDropdownDisabled?: boolean;
}

export const CircleSelector: React.FC<CircleSelectorProps> = ({
    circles,
    selectedCircleIds,
    onSelectionChange,
    searchPlaceholder = 'Search',
    dropdownPlaceholder = 'No team selected',
    searchDebounceMs = 250,
    isMultiSelect,
    isDropdownDisabled = false,
}) => {
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
        const timer = setTimeout(
            () => setDebouncedSearch(search),
            searchDebounceMs,
        );
        return () => clearTimeout(timer);
    }, [search, searchDebounceMs]);

    const filteredCircles = circles.filter((circle) =>
        circle.name.toLowerCase().includes(debouncedSearch.toLowerCase()),
    );

    const dropdownOptions = filteredCircles.map((circle) => ({
        key: circle.id,
        text: circle.name,
        selected: selectedCircleIds.includes(circle.id),
    }));

    const handleSelectionChange = (
        _ev: React.FormEvent<HTMLDivElement>,
        option?: IDropdownOption,
    ) => {
        if (!option) return;

        let newSelectedCircles: Circle[];

        if (isMultiSelect) {
            if (option.selected) {
                const newCircle = circles.find(
                    (c) => c.id === String(option.key),
                );
                if (newCircle && !selectedCircleIds.includes(newCircle.id)) {
                    newSelectedCircles = [
                        ...circles.filter((c) =>
                            selectedCircleIds.includes(c.id),
                        ),
                        newCircle,
                    ];
                } else {
                    return;
                }
            } else {
                newSelectedCircles = circles.filter(
                    (c) =>
                        selectedCircleIds.includes(c.id) &&
                        c.id !== String(option.key),
                );
            }
        } else {
            const optionId = String(option.key);
            const isCurrentlySelected = selectedCircleIds.includes(optionId);

            if (isCurrentlySelected) {
                newSelectedCircles = [];
            } else {
                const newCircle = circles.find((c) => c.id === optionId);
                if (newCircle) {
                    newSelectedCircles = [newCircle];
                } else {
                    return;
                }
            }
        }

        onSelectionChange(newSelectedCircles);
    };

    const handleSearchChange = (
        _ev?: React.ChangeEvent<HTMLInputElement>,
        value?: string,
    ) => {
        setSearch(value ?? '');
    };

    const handleSearchClear = () => {
        setSearch('');
    };

    return (
        <>
            <SearchBox
                placeholder={searchPlaceholder}
                onChange={handleSearchChange}
                onClear={handleSearchClear}
                disabled={isDropdownDisabled}
            />
            <Dropdown
                responsiveMode={2}
                multiSelect={isMultiSelect}
                placeholder={dropdownPlaceholder}
                options={dropdownOptions}
                onChange={handleSelectionChange}
                disabled={isDropdownDisabled}
            />
        </>
    );
};
